import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getProfile } from "../services/profile.service";
import { RiLoader2Fill } from "react-icons/ri";

const PublicProfileScreen = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const result = await getProfile(uid);
        
        if (result.status) {
          setProfile(result.data);
        } else {
          setError("This user hasn't set up their matching profile yet.");
        }
      } catch (err) {
        setError("Failed to load profile.");
      } finally {
        setLoading(false);
      }
    };

    if (uid) fetchUserProfile();
  }, [uid]);

  if (loading) return  <RiLoader2Fill className="animate-spin text-2xl text-primary mx-auto my-8" />;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <h2 className="text-xl font-bold text-gray-700 mb-2">Profile Not Found</h2>
        <p className="text-gray-500 mb-6">{error}</p>
        <button 
          onClick={() => navigate(-1)} 
          className="px-6 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition"
        >
          Go Back
        </button>
      </div>
    );
  }

  const initial = profile?.username?.charAt(0).toUpperCase() || "R";

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-6 text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-24 bg-primary/10"></div>
        
        <div className="relative z-10 flex flex-col items-center mt-6">
          <div className="w-24 h-24 bg-primary text-white rounded-full flex items-center justify-center text-4xl font-bold border-4 border-white shadow-md mb-4">
            {initial}
          </div>
          <h2 className="text-2xl font-bold text-gray-900">{profile?.username}</h2>
          
          <div className="flex flex-wrap justify-center items-center gap-x-2 gap-y-1 mt-2 text-gray-600 text-sm">
            <span className="capitalize">{profile?.gender}</span>
            <span>•</span>
            <span>{profile?.ageBracket} yrs</span>
            {profile?.nationality && (
              <>
                <span>•</span>
                <span>{profile.nationality}</span>
              </>
            )}
            {profile?.religion && (
              <>
                <span>•</span>
                <span>{profile.religion}</span>
              </>
            )}
            {profile?.workArrangement && (
              <>
                <span>•</span>
                <span>{profile.workArrangement}</span>
              </>
            )}
          </div>
          
          <div className="mt-6">
            <button 
             onClick={() => {
                  navigate("/dashboard/messages", {
                    state: {
                      targetUid: uid,
                    },
                  });
                }}
            className="px-8 py-2.5 bg-primary text-white rounded-full font-medium shadow-sm hover:shadow-md hover:bg-opacity-90 transition-all">
              Message {profile?.username}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 mb-6">
        <h3 className="text-lg font-bold text-gray-900 border-b border-gray-100 pb-4 mb-6">Lifestyle & Preferences</h3>
        
        {/* New Quick Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8 bg-gray-50 p-4 rounded-xl border border-gray-100">
          <div>
            <p className="text-xs text-gray-500 mb-1">Looking for</p>
            <p className="font-medium text-gray-800 text-sm">
              {profile?.ruumieGenderPreference === "Any" 
                ? "Any Gender" 
                : `${profile?.ruumieGenderPreference} Ruumie`}
            </p>
          </div>
          {profile?.smoking && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Smoking</p>
              <p className="font-medium text-gray-800 text-sm">{profile.smoking}</p>
            </div>
          )}
          {profile?.pets && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Pets</p>
              <p className="font-medium text-gray-800 text-sm">{profile.pets}</p>
            </div>
          )}
          {profile?.cooking && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Cooking</p>
              <p className="font-medium text-gray-800 text-sm">{profile.cooking}</p>
            </div>
          )}
          {profile?.houseParties && (
            <div>
              <p className="text-xs text-gray-500 mb-1">House Parties</p>
              <p className="font-medium text-gray-800 text-sm">{profile.houseParties}</p>
            </div>
          )}
          {profile?.currentOccupants && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Current Occupants</p>
              <p className="font-medium text-gray-800 text-sm">{profile.currentOccupants}</p>
            </div>
          )}
        </div>

        <div className="mb-8">
          <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            Other Habits
          </h4>
          <div className="flex flex-wrap gap-2">
            {profile?.habits?.length > 0 ? (
              profile.habits.map((habit, i) => (
                <span key={i} className="px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-full text-sm font-medium">
                  {habit}
                </span>
              ))
            ) : (
              <p className="text-sm text-gray-400 italic">No extra habits listed.</p>
            )}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500"></span>
            Dealbreakers
          </h4>
          <div className="flex flex-wrap gap-2">
            {profile?.dislikes?.length > 0 ? (
              profile.dislikes.map((dislike, i) => (
                <span key={i} className="px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-full text-sm font-medium">
                  {dislike}
                </span>
              ))
            ) : (
              <p className="text-sm text-gray-400 italic">No dealbreakers listed.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicProfileScreen;